/**
 * confidence-utils.js
 *
 * Pure helpers for computing GPS delivery confidence level.
 *
 * Accuracy tiers:
 *   Good     — both coordAccuracy AND photoAccuracy are ≤ GOOD_ACCURACY_THRESHOLD
 *   Poor     — both coordAccuracy AND photoAccuracy are > POOR_ACCURACY_THRESHOLD
 *   Moderate — everything in between (at least one is OK)
 *
 * Distance tiers:
 *   Low      — distance ≤ MAX_ACCEPTABLE_DISTANCE
 *   High     — distance >  MAX_ACCEPTABLE_DISTANCE
 */

import config from "./geo-config.json";

// ----------------------------------------------------------------------
// THRESHOLDS (METRES)
// ----------------------------------------------------------------------

// 1. DISTANCE Threshold (Global)
//    Low  <= 5m
//    High >  5m
export const MAX_ACCEPTABLE_DISTANCE = 5;

// ----------------------------------------------------------------------
// HELPERS
// ----------------------------------------------------------------------

/**
 * Returns accuracy tier based on country-specific thresholds from config.
 * Both scan & photo accuracy must meet the criteria.
 *
 * @param {number} coordAcc - scan GPS accuracy
 * @param {number} photoAcc - photo GPS accuracy
 * @param {number} [lat]    - scan latitude (for country detection)
 * @param {number} [lon]    - scan longitude
 * @returns {"good"|"moderate"|"poor"}
 */
export function getAccuracyTier(coordAcc, photoAcc, lat, lon) {
    let tier = "moderate";
    let matchedCountry = null;
    const { GEO_COORDINATES } = config;

    const updateTierBasedOnCountry = (country) => {
        const { accuracyGood, accuracyPoor } = GEO_COORDINATES[country];
        if (coordAcc <= accuracyGood && photoAcc <= accuracyGood) {
            tier = "good";
        } else if (coordAcc > accuracyPoor && photoAcc > accuracyPoor) {
            tier = "poor";
        }
    };

    if (lat != null && lon != null) {
        for (const country in GEO_COORDINATES) {
            const { minLat, maxLat, minLon, maxLon } = GEO_COORDINATES[country];
            if (lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon) {
                matchedCountry = country;
                updateTierBasedOnCountry(country);
                break;
            }
        }
    }

    if (matchedCountry === null) {
        updateTierBasedOnCountry("USA"); // Default
    }

    return tier;
}

/**
 * @param {number} distanceMetres
 * @returns {"low"|"high"}
 */
export function getDistanceTier(distanceMetres) {
    return distanceMetres <= MAX_ACCEPTABLE_DISTANCE ? "low" : "high";
}

/**
 * Truth table from spec:
 *
 * | distance | accuracy | level  | action         | flag  |
 * |----------|----------|--------|----------------|-------|
 * | low      | good     | High   | accept         | false |
 * | low      | moderate | Medium | accept_log     | true  |
 * | low      | poor     | Low    | flag           | true  |
 * | high     | good     | High   | reject         | true  |
 * | high     | moderate | Low    | reject_review  | true  |
 * | high     | poor     | Low    | reject_flag    | true  |
 *
 * Note: "reject" here means warn + flag — the upload is NEVER blocked.
 *
 * @param {"low"|"high"} distanceTier
 * @param {"good"|"moderate"|"poor"} accuracyTier
 * @returns {{ level: "High"|"Medium"|"Low", action: string, flag: boolean }}
 */
export function computeConfidence(distanceTier, accuracyTier) {
    if (distanceTier === "low") {
        if (accuracyTier === "good") return { level: "High", action: "accept", flag: false };
        if (accuracyTier === "moderate") return { level: "Medium", action: "accept_log", flag: true };
    /* poor */                       return { level: "Low", action: "flag", flag: true };
    }
    /* high distance */
    // Good GPS + far distance = GPS *confirms* wrong location → High confidence in the rejection
    if (accuracyTier === "good") return { level: "High", action: "reject", flag: true };
    if (accuracyTier === "moderate") return { level: "Low", action: "reject_review", flag: true };
  /* poor */                         return { level: "Low", action: "reject_flag", flag: true };
}

export function evaluateDeliveryConfidence(distanceMetres, coordAccuracy, photoAccuracy, originLat, originLon) {
    // Distance tier is now global (5m)
    const distanceTier = getDistanceTier(distanceMetres);

    // Accuracy tier depends on country (pass origin lat/lon)
    const accuracyTier = getAccuracyTier(coordAccuracy, photoAccuracy, originLat, originLon);

    const result = computeConfidence(distanceTier, accuracyTier);

    // Return threshold for audit (it's always 5 now)
    return { distanceTier, accuracyTier, threshold: MAX_ACCEPTABLE_DISTANCE, ...result };
}
